'use client';

import { useSyncExternalStore, type RefObject } from 'react';
import type { DesignToken, ThemeName } from '@roomtreatment/design';
import { THEME_ATTR, themeOf, tokensFor } from './tokens';

/**
 * React's view of the theme, for components that paint outside CSS (canvas,
 * WebGL, three.js). See `tokens.ts` for why those need it and the DOM doesn't.
 *
 * Its one job beyond `themeOf` is to RE-RENDER when the theme flips, so a
 * canvas redraws and a three.js material rebuilds. One MutationObserver for
 * the whole document serves every subscriber; theme flips are rare, and
 * waking every canvas to re-read its own ancestor is cheap.
 */

const listeners = new Set<() => void>();
let observer: MutationObserver | null = null;

function subscribe(onChange: () => void) {
  listeners.add(onChange);
  if (!observer) {
    observer = new MutationObserver(() => listeners.forEach((l) => l()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: [THEME_ATTR],
      subtree: true,
    });
  }
  return () => {
    listeners.delete(onChange);
    if (listeners.size === 0) {
      observer?.disconnect();
      observer = null;
    }
  };
}

/**
 * The theme in force at `ref`. Before the element mounts it reads the
 * document root; on the server it is 'light', matching what SSR paints.
 */
export function useTheme(ref?: RefObject<Element | null>): ThemeName {
  return useSyncExternalStore(
    subscribe,
    () => themeOf(ref?.current),
    () => 'light',
  );
}

/** The resolved token set at `ref`. */
export function useTokens(ref?: RefObject<Element | null>): Record<DesignToken, string> {
  return tokensFor(useTheme(ref));
}
